import React, { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Badge from "@material-ui/core/Badge";
import Popover from "@material-ui/core/Popover";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import Button from "@material-ui/core/Button";
import NetworkCheckIcon from "@material-ui/icons/NetworkCheck";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConnectionHealthChip from "../ConnectionHealthChip";
import { formatApproxTime } from "../../pages/SuperConnectionsDashboard/superConnectionsUtils";

// Só leitura: a única chamada de rede deste componente é
// GET /super/connections/summary. Nunca importa nem chama nada de
// libs/wbot, StartWhatsAppSession, DeleteBaileysService ou qualquer rota de
// sessão/QR/restart/disconnect — o botão "Ver todas as conexões" só navega
// para o dashboard já existente (Entrega 2), também só leitura.
const POLL_INTERVAL_MS = 60000;

const EMPTY_SUMMARY = {
  total: 0,
  healthy: 0,
  connecting: 0,
  awaitingScan: 0,
  alert: 0,
  unmonitored: 0,
  alerts: []
};

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    width: 340,
    maxWidth: "90vw"
  },
  countsGrid: {
    marginBottom: theme.spacing(1)
  },
  alertsList: {
    maxHeight: 260,
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  errorNotice: {
    // Cor calculada a partir do tema (nunca fixa) — legível tanto no claro
    // quanto no escuro, mesmo padrão de decisão já usado no restante do
    // módulo de conexões globais.
    color: theme.palette.type === "dark" ? "#ffb4b4" : "#b71c1c",
    fontSize: "0.75rem",
    marginTop: theme.spacing(1)
  }
}));

const SuperConnectionsHeaderAlert = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const isSuper = user?.super === true;

  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [hasError, setHasError] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const mountedRef = useRef(true);
  // Sempre lido dentro de fetchSummary — nunca o valor fechado de `isSuper`
  // no momento em que a função foi criada, porque a Promise em voo pode
  // resolver depois que o usuário deixou de ser super (logout/downgrade).
  //
  // Atribuído diretamente durante a renderização (não dentro de um
  // useEffect) — um efeito só roda DEPOIS do commit, e nesse intervalo uma
  // resposta que já estava em voo poderia encontrar o valor antigo. Uma
  // atribuição de ref durante a renderização é síncrona com o React
  // processar o novo valor de `isSuper`, sem essa janela.
  const isSuperRef = useRef(isSuper);
  isSuperRef.current = isSuper;
  // Trava de requisição em andamento — impede que o intervalo de 60s e um
  // clique no ícone disparem chamadas sobrepostas.
  const inFlightRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isSuper) {
      // Logout ou downgrade para usuário comum: nunca deixa o popover
      // aberto mostrando dado de superadministrador.
      setAnchorEl(null);
    }
  }, [isSuper]);

  const fetchSummary = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      const { data } = await api.get("/super/connections/summary");
      if (mountedRef.current && isSuperRef.current) {
        setSummary(data);
        setHasError(false);
      }
    } catch (err) {
      // Falha automática é sempre silenciosa — nunca um toast repetido a
      // cada atualização. O último resumo válido é mantido; só marcamos um
      // indicador discreto para exibição dentro do próprio popover.
      if (mountedRef.current && isSuperRef.current) {
        setHasError(true);
      }
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isSuper) return undefined;

    fetchSummary();
    const intervalId = setInterval(() => {
      fetchSummary();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [isSuper, fetchSummary]);

  if (!isSuper) return null;

  const handleOpen = event => {
    setAnchorEl(event.currentTarget);
    fetchSummary();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewAll = () => {
    setAnchorEl(null);
    history.push("/superConnections");
  };

  const open = Boolean(anchorEl);
  const alertCount = summary.alert || 0;

  return (
    <div>
      <IconButton
        aria-label={i18n.t("superConnections.summary.ariaLabel")}
        onClick={handleOpen}
        style={{ color: "white" }}
      >
        <Badge badgeContent={alertCount} color="error" max={99} invisible={alertCount === 0}>
          <NetworkCheckIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Paper className={classes.paper}>
          <Typography variant="subtitle1" gutterBottom>
            {i18n.t("superConnections.summary.title")}
          </Typography>

          <Grid container spacing={1} className={classes.countsGrid}>
            <Grid item xs={4}>
              <Typography variant="caption" display="block">
                {i18n.t("superConnections.summary.total")}
              </Typography>
              <Typography variant="body2">{summary.total}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" display="block">
                {i18n.t("superConnections.summary.healthy")}
              </Typography>
              <Typography variant="body2">{summary.healthy}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" display="block">
                {i18n.t("superConnections.summary.connecting")}
              </Typography>
              <Typography variant="body2">{summary.connecting}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" display="block">
                {i18n.t("superConnections.summary.awaitingScan")}
              </Typography>
              <Typography variant="body2">{summary.awaitingScan}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" display="block">
                {i18n.t("superConnections.summary.alert")}
              </Typography>
              <Typography variant="body2">{summary.alert}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="caption" display="block">
                {i18n.t("superConnections.summary.unmonitored")}
              </Typography>
              <Typography variant="body2">{summary.unmonitored}</Typography>
            </Grid>
          </Grid>

          <Divider />

          <List dense className={classes.alertsList}>
            {summary.alerts.length === 0 && (
              <ListItem>
                <ListItemText primary={i18n.t("superConnections.summary.noAlerts")} />
              </ListItem>
            )}
            {summary.alerts.map(row => (
              <ListItem key={row.whatsappId} divider>
                <ListItemText
                  primary={`${row.companyName} — ${row.name}`}
                  secondary={`${row.status || "-"} · ${formatApproxTime(
                    row.approxTimeInStatusSeconds,
                    (key, options) => i18n.t(key, options)
                  )}`}
                />
                <ConnectionHealthChip health={row.health} />
              </ListItem>
            ))}
          </List>

          {hasError && (
            <Typography className={classes.errorNotice}>
              {i18n.t("superConnections.summary.updateError")}
            </Typography>
          )}

          <Button fullWidth color="primary" onClick={handleViewAll} style={{ marginTop: 8 }}>
            {i18n.t("superConnections.summary.viewAll")}
          </Button>
        </Paper>
      </Popover>
    </div>
  );
};

export default SuperConnectionsHeaderAlert;
